import { fetchJobDataForForm } from "@/modules/work/jobs/form/read";
import { useNavigate } from "react-router-dom";
import { getRequiredFields } from "components/form/utils";
import { jobSchema } from "@/modules/work/jobs/form/schema";
import { useCreateJob } from "@/modules/work/jobs/create/mutation";
import { useForm } from "react-hook-form";
import { useMemo } from "react";
import { useUpdateJob } from "@/modules/work/jobs/update/mutation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFetch } from "@/hooks/useFetch";
import { jobKeys } from "components/job/queries";
import { useParams } from "react-router-dom";
import { fetchJobMilestoneCount } from "@/modules/work/jobs/api";
import { ROUTE } from "@/lib/utils";

export const useJobForm = ({ defaultProjectId, type }) => {
  const createJobMutation = useCreateJob();
  const updateJobMutation = useUpdateJob();
  const navigate = useNavigate();
  const { jobId } = useParams();

  const form = useForm({
    resolver: zodResolver(jobSchema),
    defaultValues: {
      name: "",
      status: "draft",
      type: type || "",
      description: "",
      margin_calculator_link: "",
      project: defaultProjectId || "",
      contract: "",
      disable_job_logging: type === "hourly" || type === "bucket",
    },
  });

  const {
    data: milestoneCount,
    isLoading,
    isError,
  } = useFetch({
    queryKey: [jobKeys.milestones(jobId)],
    queryFn: () => fetchJobMilestoneCount({ jobId }),
    enabled: !!jobId,
  });

  const requiredFields = useMemo(
    () => getRequiredFields(jobSchema),
    [jobSchema]
  );

  const handleCreate = async (context) => {
    const response = await createJobMutation.mutateAsync({
      values: context.values,
    });

    navigate(ROUTE.JOB(response.id));
  };

  async function handleRead(id) {
    const response = await fetchJobDataForForm({ id });

    const values = {
      ...response,
      description: response.description || "",
      margin_calculator_link: response.margin_calculator_link || "",
      contract: response.contract || response.contract?.id || null,
    };
    return values;
  }

  async function handleUpdate(id, context) {
    return await updateJobMutation.mutateAsync({
      id,
      values: context.dirtyValues,
    });
  }

  async function handleArchive() {
    const values = { status: "archived" };
    const response = await updateJobMutation.mutateAsync({ id: jobId, values });

    return response;
  }

  async function handleCancel() {
    const values = { status: "cancelled" };
    const response = await updateJobMutation.mutateAsync({ id: jobId, values });

    return response;
  }

  async function handleClose() {
    const values = { status: "closed" };
    const response = await updateJobMutation.mutateAsync({ id: jobId, values });

    return response;
  }

  return {
    form,
    requiredFields,
    milestoneCount,
    handleCreate,
    handleRead,
    handleUpdate,
    handleArchive,
    handleCancel,
    handleClose,
  };
};
